import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { getDeviceIdentity } from "../lib/device";

const AuthContext = createContext(null);

export const ROLES = {
  super_admin: "Super Admin",
  admin: "Administrador",
  supervisor: "Supervisor",
  cashier: "Cajero",
  auditor: "Auditor",
};

// Role groupings (explicit set matching — must mirror backend require_roles)
export const ROLE_GROUPS = {
  MANAGE: ["super_admin", "admin"],
  SUPERVISE: ["super_admin", "admin", "supervisor"],
  AUDIT: ["super_admin", "admin", "supervisor", "auditor"],
  OPS: ["super_admin", "admin", "supervisor", "cashier"],
  ALL: ["super_admin", "admin", "supervisor", "cashier", "auditor"],
  SUPER: ["super_admin"],
};

const CONSORTIUM_KEY = "sally_active_consortium_id";

/** Persist which consortium the current session should scope backend calls to. */
async function syncConsortiumFor(u) {
  if (!u) {
    localStorage.removeItem(CONSORTIUM_KEY);
    return "";
  }
  if (u.role === "super_admin") {
    // Keep whatever the user already picked, otherwise auto-pick the oldest consortium
    // so the first page load works without a 400 "selecciona consorcio" error.
    const current = localStorage.getItem(CONSORTIUM_KEY);
    if (current) return current;
    try {
      const { data } = await api.get("/consortiums");
      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) {
        // Backend returns newest first; pick the oldest for a stable default.
        const chosen = list[list.length - 1].id;
        localStorage.setItem(CONSORTIUM_KEY, chosen);
        return chosen;
      }
    } catch (e) {
      console.warn("No se pudo auto-seleccionar consorcio", e);
    }
    return "";
  }
  if (u.consortium_id) {
    localStorage.setItem(CONSORTIUM_KEY, u.consortium_id);
    return u.consortium_id;
  }
  localStorage.removeItem(CONSORTIUM_KEY);
  return "";
}

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);
  const [activeConsortiumId, setActiveConsortiumIdState] = useState(
    () => localStorage.getItem(CONSORTIUM_KEY) || ""
  );

  const setActiveConsortiumId = (cid) => {
    if (cid) {
      localStorage.setItem(CONSORTIUM_KEY, cid);
    } else {
      localStorage.removeItem(CONSORTIUM_KEY);
    }
    setActiveConsortiumIdState(cid || "");
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!mounted) return;
        const cid = await syncConsortiumFor(data);
        if (!mounted) return;
        setUser(data);
        setActiveConsortiumIdState(cid || "");
      } catch {
        if (mounted) setUser(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (usernameOrEmail, password) => {
    try {
      const identity = await getDeviceIdentity();
      const val = (usernameOrEmail || "").trim();
      // Detect if user typed an email; otherwise send as username
      const isEmail = val.includes("@");
      const payload = isEmail
        ? { email: val.toLowerCase(), password, ...identity }
        : { username: val.toLowerCase(), password, ...identity };
      const { data } = await api.post("/auth/login", payload);
      if (data.access_token) {
        localStorage.setItem("sally_access_token", data.access_token);
      }
      const cid = await syncConsortiumFor(data.user);
      setUser(data.user);
      setActiveConsortiumIdState(cid || "");
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout best-effort failed", e);
    }
    localStorage.removeItem("sally_access_token");
    localStorage.removeItem(CONSORTIUM_KEY);
    setActiveConsortiumIdState("");
    setUser(false);
  };

  const hasRole = (...roles) => {
    if (!user) return false;
    // Explicit set membership: user.role must be in the provided list.
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        hasRole,
        activeConsortiumId,
        setActiveConsortiumId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
